<template>
  <ModalLayout full title="Dashboards Dwell / How it works">
    <div
      class="flex rounded-t-[10px] bg-white px-[24px] pb-[26px] pt-[28px] sm:px-[40px] sm:pt-[30px]"
    >
      <div class="mr-[24px] overflow-hidden">
        <div class="text-[13px] leading-5 text-gpx_gray-200">
          How we calculate data
        </div>
        <div class="text-ellipsis whitespace-nowrap">
          <div
            class="overflow-hidden text-ellipsis text-[24px] font-semibold leading-7 text-gpx_black-50"
          >
            How We Calculate Dwell
          </div>
        </div>
      </div>
      <button
        class="ml-auto flex h-[32px] w-[32px] shrink-0 cursor-pointer items-center justify-center rounded-full bg-gpx_gray-900"
        @click="$emit('close')"
      >
        <XMarkIcon
          class="heroicon-stroke-w-1.4 h-[16px] w-[16px] text-gpx_gray-200"
        />
      </button>
    </div>
    <div
      class="border-t bg-white px-[24px] text-[15px] leading-5 text-gpx_black-200 sm:px-[40px]"
    >
      <div class="mb-4 pt-[23px]">
        The dwell dashboard calculates the performance of trackable assets in
        your geofences to help identify any potential problems. Multiple users
        on an account can look at different geofences or different lookback
        periods simultaneously.
      </div>

      <div class="mb-4">
        <div
          class="mb-1 text-[15px] font-semibold uppercase leading-6 text-gpx_black-400"
        >
          Look-back period
        </div>
        <div class="px-6">
          <UIList>
            <UIListItem>
              Dwell looks at the last 30, 60 or 90 days to calculate the
              metrics.
            </UIListItem>
          </UIList>
        </div>
      </div>

      <div class="mb-4">
        <div
          class="mb-1 text-[15px] font-semibold uppercase leading-6 text-gpx_black-400"
        >
          Setting SLAs
        </div>
        <div class="px-6">
          <UIList>
            <UIListItem>
              You can set custom parameters for each geofence by editing the
              gray fields in the left columns. These inline edits allow you to
              update your geofence parameters. The Dwell Dashboard will
              automatically update the metrics coloring based on inline edits.
            </UIListItem>
          </UIList>
        </div>
      </div>

      <div class="mb-4">
        <div
          class="mb-1 text-[15px] font-semibold uppercase leading-6 text-gpx_black-400"
        >
          Metrics
        </div>
        <div class="px-6">
          <UIList>
            <UIListItem>
              Assets in Geofence: This metric counts the number of trackers that
              are currently located in the geofence.
            </UIListItem>
            <UIListItem>
              Average Days Assets in Geofence: This metric calculates the
              average number of days that trackers have spent in the geofence
              during the look-back period.
            </UIListItem>
            <UIListItem>
              Average Dwell Time as % of SLA: This metric calculates the average
              dwell time for trackers as a percentage of the agreed-upon service
              level agreement (SLA).
            </UIListItem>
            <UIListItem>
              Days in Geofence: This metric calculates the average number of
              days that each tracker has spent in the geofence, regardless of
              when the tracker entered the geofence. If a tracker entered the
              geofence prior to the look-back period, only the time spent since
              the start of the look-back period is calculated for dwell.
            </UIListItem>
            <UIListItem>
              Number of Assets Exceeding Dwell SLA: This will only be calculated
              when a Dwell SLA is set, otherwise it defaults to zero.
            </UIListItem>
          </UIList>
        </div>
      </div>

      <div class="mb-4">
        <div
          class="mb-1 text-[15px] font-semibold uppercase leading-6 text-gpx_black-400"
        >
          Coloring
        </div>
        <div class="px-6">
          <UIList>
            <UIListItem>
              <UIDotBadge
                variant="success"
                class="!py-[1px] translate-y-[-2px]"
              >
                Green
              </UIDotBadge>
              : Performance against set dwell SLA is equal to or less than 75%
              of the SLA. For Minimum and Maximum # of assets, they will appear
              green only if they are above, below or between the SLAs set.
            </UIListItem>
            <UIListItem>
              <UIDotBadge
                variant="warning"
                class="!py-[1px] translate-y-[-2px]"
              >
                Yellow
              </UIDotBadge>
              : Performance against set SLA is more than 75% and less than 100%
              of the SLA. For Minimum and Maximum # of assets, yellow is used.
            </UIListItem>
            <UIListItem>
              <UIDotBadge variant="error" class="!py-[1px] translate-y-[-2px]">
                Red
              </UIDotBadge>
              : Average dwell time is greater than 100% of the SLA. For Minimum
              and Maximum # assets, they will display as red if they are under
              the minimum or over the maximum SLA.
            </UIListItem>
            <UIListItem>
              <UIDotBadge
                variant="default"
                class="!py-[1px] translate-y-[-2px]"
              >
                White
              </UIDotBadge>
              : If an SLA is not set, values will calculate but colors will not
              be displayed.
            </UIListItem>
          </UIList>
        </div>
      </div>

      <div class="mb-4">
        <div
          class="mb-1 text-[15px] font-semibold uppercase leading-6 text-gpx_black-400"
        >
          See Trackers in Geofence
        </div>
        <div class="px-6">
          <UIList>
            <UIListItem>
              Any geofence with trackers in it is expandable to let you view the
              asset trackers in that geofence and show you key details like Days
              in Geofence, last entrance time stamp, and option to link to the
              individual tracker page.
            </UIListItem>
          </UIList>
        </div>
      </div>

      <div>
        <div
          class="mb-1 text-[15px] font-semibold uppercase leading-6 text-gpx_black-400"
        >
          Exceptions
        </div>
        <div class="px-6">
          <UIList>
            <UIListItem>
              If a tracker enters the geofence prior to the start of the look
              back period - i.e. entered 35 days ago but the lookback is for 30
              days - only the time since the start of the lookback period is
              calculated as dwell.
            </UIListItem>
          </UIList>
        </div>
      </div>

      <div class="pb-[40px]"></div>
    </div>
    <Button size="modal" variant="secondary" @click="$emit('close')">
      Got it
    </Button>
  </ModalLayout>
</template>

<script setup>
import UIDotBadge from '@/components/ui/UIDotBadge/UIDotBadge.vue';
import ModalLayout from '@/layouts/ModalLayout.vue';
</script>
